import type { ApiKitTrip, Pcl } from '@tuic/api-kit';
import { getNightsBetween, toRezonedDateTime } from '@lib/date';
import { DateTime } from 'luxon';
import { PhasedBooking } from '@lib/phased-booking';
import { getNowBasedOnBooking } from '@lib/booking';

const getEarliestStartDateAsString = (
  trips: ApiKitTrip[],
): string | undefined => {
  // Let's reduce the trips the earliest date.
  return trips.reduce<string | undefined>((acc, curr) => {
    // If there is no startDateTime in the currently iterated trip, we can't do anything.
    if (typeof curr.startDateTime === 'undefined') {
      return acc;
    }

    // During the first iteration we need to define any date for later comparison.
    if (!acc) {
      return curr.startDateTime;
    }

    // If the currently iterated trip's startDateTime is earlier than the current one, return it.
    if (new Date(acc).getTime() > new Date(curr.startDateTime).getTime()) {
      return curr.startDateTime;
    }

    // Otherwise return the current one.
    return acc;
  }, undefined);
};

export const getEarliestStartDate = (
  booking: PhasedBooking,
  keepLocalTime = false,
): DateTime<true> | undefined => {
  const earliest = getEarliestStartDateAsString(booking.trips);

  if (!earliest) {
    return undefined;
  }

  return toRezonedDateTime(earliest, keepLocalTime);
};

/**
 * Returns the latest end date as string from given trip collection.
 */
const getLatestEndDateAsString = (trips: ApiKitTrip[]): string | undefined => {
  return trips.reduce<string | undefined>((acc, curr) => {
    // If there is no endDateTime in the currently iterated trip, we can't do anything.
    if (typeof curr.endDateTime === 'undefined') {
      return acc;
    }

    // During the first iteration we need to define any date for later comparison.
    if (!acc) {
      return curr.endDateTime;
    }

    // If the currently iterated trip's endDateTime is later than the current one, return it.
    if (new Date(acc).getTime() < new Date(curr.endDateTime).getTime()) {
      return curr.endDateTime;
    }

    // Otherwise return the current one.
    return acc;
  }, undefined);
};

/**
 * Returns the latest end date from given trip collection.
 */
export const getLatestEndDate = (
  booking: PhasedBooking,
  keepLocalTime = false,
): DateTime<true> | undefined => {
  const latest = getLatestEndDateAsString(booking.trips);

  if (!latest) {
    return undefined;
  }

  return toRezonedDateTime(latest, keepLocalTime);
};

/**
 * Returns the total nights of given booking.
 */
export const getNumberOfTotalNights = (
  booking: PhasedBooking,
): number | undefined => {
  return booking.trips.reduce((sum, trip) => {
    if (
      typeof trip.startDateTime === 'undefined' ||
      typeof trip.endDateTime === 'undefined'
    ) {
      return sum;
    }

    return sum + getNightsBetween(trip.startDateTime, trip.endDateTime);
  }, 0);
};

/**
 * Checks if given trips collection already started.
 */
export const tripStarted = (booking: PhasedBooking) => {
  const earliest = getEarliestStartDate(booking);

  if (!earliest) {
    return false;
  }

  return DateTime.now() > earliest.startOf('day');
};

/**
 * Checks if given trips collection is currently ongoing.
 */
export const tripOngoing = (booking: PhasedBooking) => {
  return tripStarted(booking) && tripEnded(booking) === false;
};

/**
 * Checks if given trips collection ended.
 */
export const tripEnded = (booking: PhasedBooking) => {
  const latestEndDate = getLatestEndDate(booking);

  if (!latestEndDate) {
    return false;
  }

  return DateTime.now() > latestEndDate.endOf('day');
};

/**
 * Get the number of days until the journey start.
 *
 * @param booking The booking to determine the journey start date from
 * @returns The number of days until the journey start or undefined
 *          if the journey start date could not be determined
 */
export const getDaysBeforeJourneyStart = (
  booking: PhasedBooking,
): number | undefined => {
  const earliestStartDate = getEarliestStartDate(booking);

  if (!earliestStartDate) {
    return undefined;
  }

  return Math.floor(
    earliestStartDate.diff(getNowBasedOnBooking(booking), 'days').as('days'),
  );
};
